import {
  Box,
  Center,
  chakra,
  HStack,
  Link,
  LinkBox,
  LinkOverlay,
  Stack,
} from '@cardboard-ui/react';
import { FileIcon } from 'components/icons/FileIcon';
import { GridItemSkeleton } from 'components/Item/GridItem';
import {
  DocumentThumbnail,
  ImageThumbnail,
} from 'components/Item/ItemMenu/components/StylishThumbnail';
import { ListItemMainText } from 'components/ListItem/ListItemMainText';
import { ListItemSecondaryText } from 'components/ListItem/ListItemSecondaryText';
import React from 'react';
import {
  AppFileTypes,
  mimeTypeToAppType,
  mimeTypeToUserFriendlyString,
} from 'utils/file/MIMETypeUtils';
import { graphql, useFragment } from 'utils/graphClient';
import { AnnouncementAttachmentCard_details$key } from './__generated__/AnnouncementAttachmentCard_details.graphql';

const fragment = graphql`
  fragment AnnouncementAttachmentCard_details on AnnouncementAttachment {
    id
    thumbnail {
      url
    }
    content {
      __typename
      ... on File {
        __typename
        name
        mimeType
        url
      }
    }
  }
`;

interface AnnouncementAttachmentCardProps {
  node: AnnouncementAttachmentCard_details$key;
}

export const AnnouncementAttachmentCard = ({
  node,
}: AnnouncementAttachmentCardProps) => {
  const attachmentNode = useFragment(fragment, node);

  if (attachmentNode.content.__typename !== 'File') {
    return null;
  }

  const name =
    attachmentNode.content?.__typename === 'File'
      ? attachmentNode.content?.name
      : '';
  const description =
    attachmentNode.content?.__typename === 'File'
      ? mimeTypeToUserFriendlyString(attachmentNode.content?.mimeType)
      : '';

  const thumbnailSrc =
    attachmentNode.thumbnail?.url ||
    (mimeTypeToAppType(attachmentNode.content.mimeType) === AppFileTypes.IMAGE
      ? attachmentNode.content.url
      : null);

  return (
    <LinkBox
      sx={{
        // This is a manual fix of known bug (LinkBox doesn't elevate buttons on top of it) of chakra https://github.com/chakra-ui/chakra-ui/pull/5679
        'a[href]:not(.chakra-linkbox__overlay), abbr[title], button': {
          position: 'relative',
          zIndex: 2,
        },
      }}
    >
      <GridItemSkeleton
        cover={
          <Thumbnail
            mimeType={attachmentNode.content.mimeType}
            thumbnailSrc={thumbnailSrc}
          />
        }
      >
        <HStack>
          <LinkOverlay
            as={Link}
            toItemViewer={attachmentNode.id}
            overflow="hidden"
            flex={1}
          >
            <LinkBox overflow="hidden" flexGrow={2}>
              <HStack spacing={4} minWidth={0} flexGrow={1} whiteSpace="nowrap">
                <Box>
                  {attachmentNode.content?.__typename === 'File' && (
                    <FileIcon
                      mimeType={attachmentNode.content.mimeType}
                      variant="Large"
                      color="blue.500"
                    />
                  )}
                </Box>

                <Stack
                  spacing={0}
                  whiteSpace="nowrap"
                  minWidth={0}
                  flexGrow="1"
                >
                  <LinkOverlay as={Link} toItemViewer={attachmentNode.id}>
                    <ListItemMainText>{name}</ListItemMainText>
                  </LinkOverlay>

                  <ListItemSecondaryText>
                    <chakra.span>{description}</chakra.span>
                  </ListItemSecondaryText>
                </Stack>
              </HStack>
            </LinkBox>
          </LinkOverlay>
        </HStack>
      </GridItemSkeleton>
    </LinkBox>
  );
};

const Thumbnail = ({
  thumbnailSrc,
  mimeType,
}: {
  thumbnailSrc: string | null | undefined;
  mimeType: string;
}) => {
  switch (mimeTypeToAppType(mimeType)) {
    case AppFileTypes.IMAGE:
      if (thumbnailSrc) {
        return <ImageThumbnail url={thumbnailSrc} />;
      }
      break;
    default:
      if (thumbnailSrc) {
        return <DocumentThumbnail url={thumbnailSrc} />;
      }
  }

  return (
    <Center w="100%" h="100%" fontSize="50px" color="blue.500">
      <FileIcon mimeType={mimeType} />
    </Center>
  );
};
